import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMenu } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { DumbClubAvatar } from '@sorare/core/src/components/club/DumbClubAvatar';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';

import type {
  FeaturedMainLeagueLaunchClubFilter,
  FeaturedMainLeagueLaunchClubFilterVariables,
} from './__generated__/index.graphql';

const StyledTextSwitch = styled(TextSwitch)`
  --tab-padding-x: var(--half-unit);
`;
const ALL_VALUE = 'all';

type Props = {
  leagueSlug: string;
  clubs?: LeagueLaunch['clubs'];
};

const NEW_SEASON_CARDS_SHOW_QUERY = gql`
  query FeaturedMainLeagueLaunchClubFilter($competitionSlug: String!) {
    football {
      competition(slug: $competitionSlug) {
        slug
        teams {
          nodes {
            slug
            name
            avatarUrl: pictureUrl
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FeaturedMainLeagueLaunchClubFilter,
  FeaturedMainLeagueLaunchClubFilterVariables
>;

export const ClubFilter = ({ leagueSlug, clubs: clubsProps }: Props) => {
  const { items, refine } = useMenu({
    attribute: 'active_club.long_name_with_slug',
    limit: 40,
  });

  const itemByClubSlug = items.reduce<Record<string, (typeof items)[number]>>(
    (acc, item) => {
      const [, slug] = item.label.split('|');
      acc[slug] = item;
      return acc;
    },
    {}
  );

  const { data } = useQuery(NEW_SEASON_CARDS_SHOW_QUERY, {
    variables: { competitionSlug: leagueSlug },
    skip: !!clubsProps,
  });

  const clubs = (
    clubsProps ||
    data?.football?.competition?.teams?.nodes ||
    []
  ).filter(({ slug }) => !!itemByClubSlug[slug]);

  const options = [
    {
      label: <FormattedMessage {...glossary.all} />,
      value: 'all',
    },
    ...(clubs?.map(club => ({
      label: <DumbClubAvatar size={24} club={club} />,
      value: club.slug,
    })) || []),
  ];

  const refineClub = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return;
    }
    if (value === ALL_VALUE) {
      refine('');
    } else {
      refine(itemByClubSlug[value].value);
    }
  };

  const selected =
    items.find(item => item.isRefined)?.value ||
    (items.every(({ isRefined }) => !isRefined) && ALL_VALUE);

  return (
    <StyledTextSwitch
      value={selected}
      options={options}
      onChange={refineClub}
    />
  );
};
